import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { SectionLabel, PageHero, CtaSection, BtnGhost } from "../components/common";
import { SOLUTIONS, SOLUTION_SECTORS } from "../data/site";

export default function Solutions() {
  return (
    <>
      <Seo
        title="Solutions | STERILTRON Medical Waste & Sterilization Technologies"
        description="STERILTRON solutions adapt to facility capacity and operational requirements — from hospitals and healthcare networks to industrial and public-sector projects."
        path="/solutions"
      />
      <PageHero
        label="Solutions"
        titleLines={["Engineered", "Around Your Facility"]}
        description="STERILTRON technologies are configured to your facility's capacity, utilities and regulatory framework — delivering the right level of treatment for every environment."
      />

      {/* SOLUTION CATEGORIES */}
      <section className="pt-10 md:pt-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-brand-50/60 border border-slate-200 p-6 md:p-8">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-brand-950">Not sure which solution fits?</h2>
              <p className="mt-1 text-sm text-slate-600">Compare any two solutions side by side to find the right fit for your facility.</p>
            </div>
            <BtnGhost to="/solutions/compare" testid="solutions-compare-cta">Compare Solutions</BtnGhost>
          </div>
        </div>
      </section>

      {/* SOLUTION CATEGORIES */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-20 md:space-y-28">
          {SOLUTIONS.map((s, i) => (
            <div key={s.slug} id={s.slug} className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center scroll-mt-24">
              <div className={`lg:col-span-6 ${i % 2 ? "lg:order-2" : ""}`}>
                <Reveal className="overflow-hidden border border-slate-200">
                  <img src={s.image} alt={s.title} loading="lazy" className="w-full h-[340px] md:h-[440px] object-cover" />
                </Reveal>
              </div>
              <div className={`lg:col-span-6 ${i % 2 ? "lg:order-1" : ""}`}>
                <SectionLabel>{`0${i + 1} — Solution`}</SectionLabel>
                <h2 className="mt-5 font-heading text-3xl md:text-4xl font-bold tracking-tighter text-brand-950">{s.title}</h2>
                <p className="mt-6 text-slate-700 text-base md:text-lg leading-relaxed">{s.body}</p>
                <ul className="mt-8 space-y-3">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-slate-700">
                      <span className="mt-1 flex-shrink-0 w-5 h-5 bg-brand-800 text-white flex items-center justify-center"><Check size={13} /></span>
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <BtnGhost to={`/solutions/${s.slug}`} testid={`solution-view-${s.slug}`}>View Full Solution</BtnGhost>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-16 md:py-24 bg-brand-50/40 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionLabel>By Sector</SectionLabel>
          <h2 className="mt-6 font-heading text-4xl md:text-5xl font-bold tracking-tighter text-brand-950 max-w-2xl text-balance">Solutions for every operational context</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {SOLUTION_SECTORS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.06} className="bg-white p-8 md:p-10">
                <span className="font-mono text-sm text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight text-brand-950">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </Reveal>
            ))}
            <Link to="/contact" data-testid="solutions-sector-cta" className="group bg-brand-800 text-white p-8 md:p-10 flex flex-col justify-between hover:bg-brand-900 transition-colors">
              <ArrowUpRight size={28} />
              <h3 className="mt-10 font-heading text-xl font-semibold tracking-tight">Discuss your project</h3>
            </Link>
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
